/**
 * iter212b — Admin-only compact Garage Hive integration health indicator.
 *
 * Renders a small pill in the FOH header for ADMIN users. When the
 * backend nav-status endpoint reports `needs_attention=true` a red
 * warning dot is shown; otherwise a green "all clear" dot. Clicking
 * navigates to the full Integration Health page.
 *
 * Polls once on mount and every 60 seconds thereafter — the endpoint
 * is index-only Mongo reads, no network hop to GH.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ShieldAlert, ShieldCheck, Cable } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60_000;

export default function IntegrationHealthIndicator({ user }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);

  const isAdmin = (user?.role || "").toUpperCase() === "ADMIN";

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/admin/integrations/garage-hive/nav-status`);
        if (!cancelled) setStatus(r.data);
      } catch {
        if (!cancelled) setStatus({ needs_attention: false, reason: "error" });
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin]);

  if (!isAdmin) return null;

  const attention = !!status?.needs_attention;
  const Icon = attention ? ShieldAlert : ShieldCheck;
  const wrapCls = attention
    ? "border-red-300 text-red-700 hover:bg-red-50"
    : "border-slate-300 text-slate-600 hover:bg-slate-50";

  const tooltip = attention
    ? `Garage Hive needs attention: ${status?.reason || "check the health page"}`
    : "Garage Hive integration OK";

  return (
    <button
      type="button"
      onClick={() => navigate("/app/admin/integration-health")}
      className={`relative inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-colors ${wrapCls}`}
      title={tooltip}
      data-testid="header-integration-health-indicator"
      data-attention={attention ? "true" : "false"}
    >
      <Cable className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">GH</span>
      <Icon className={`w-3.5 h-3.5 ${attention ? "text-red-600" : "text-emerald-600"}`} />
      {attention && (
        <span
          className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white animate-pulse"
          aria-hidden="true"
        />
      )}
    </button>
  );
}
